import { useCallback, useMemo } from "react";

import { computeRevisionChecksum, createNautilusStore } from "@skilitics/nautilus-core-designer-data";

import type { BackendApi } from "../backend/index.ts";
import { appConfigReducerCreate, useDeleteNodeStickyState, type NautilusDesignerAppConfig } from "../state/index.ts";
import { createReducer, publishNodeDesignsEvent } from "./createReducer.ts";
import { childNodeRemovalInterceptorCreate } from "./persistence/interceptors/childNodeRemovalInterceptor.ts";
import { effectReferenceInterceptorCreate } from "./persistence/interceptors/effectReferenceRemovalInterceptor.ts";
import { lockedValueRefTombstoneMiddlewareCreate } from "./persistence/middleware/lockedValueRefTombstoneMiddleware.ts";
import { persistWithRetries } from "./persistence/retryingPersister.ts";
import { clearUnsentActions, saveUnsentActions } from "./persistence/unsentActionsStorage.ts";

export interface NautilusStoreOptions {
  api: BackendApi;
  hostId: string;
  initialAppConfig: NautilusDesignerAppConfig;
}

export const useNautilusStore = ({
  api,
  hostId,
  initialAppConfig,
  initialAppConfig: { getManifest },
}: NautilusStoreOptions) => {
  // TODO: sticky state is a bit icky here. bring into managed form
  const deleteNodeStickyState = useDeleteNodeStickyState();
  const beforeRemove = useCallback((nodeId: number) => deleteNodeStickyState(nodeId), [deleteNodeStickyState]);

  return useMemo(
    () =>
      createNautilusStore<NautilusDesignerAppConfig>({
        publishNodeDesignsEvent,
        createNodeDesignsReducer: (options) => createReducer({ getManifest, options }),
        createAppConfigReducer: () => appConfigReducerCreate(initialAppConfig),
        onUnsentActionsChanged: {
          saveUnsentActions: (actions) => saveUnsentActions(hostId, actions),
          clearUnsentActions: () => clearUnsentActions(hostId),
        },
        sendActionToServer: async (action) => {
          switch (action.type) {
            case "APPLY_REVISION": {
              const { checksum, ...rest } = action.payload;
              await persistWithRetries(() =>
                api.applyRevision({
                  ...rest,
                  checksum: checksum ?? computeRevisionChecksum(rest.actions),
                  appVersion: APP_VERSION,
                }),
              );
              break;
            }
            case "UNDO_REVISION": {
              const { revision } = action.payload;
              await persistWithRetries(() => api.undo(revision));
              break;
            }
            case "REDO_REVISION": {
              const { revision } = action.payload;
              await persistWithRetries(() => api.redo(revision));
              break;
            }
            default:
              // @ts-expect-error exhaustive check
              throw new Error(`Unsupported action type: ${action.type}`);
          }
        },
        nodeDesignsActionInterceptors: [
          childNodeRemovalInterceptorCreate({ getManifest, beforeRemove }),
          effectReferenceInterceptorCreate({ getManifest }),
        ],
        middleware: [lockedValueRefTombstoneMiddlewareCreate()],
      }),
    [api, beforeRemove, getManifest, hostId, initialAppConfig],
  );
};
